import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { isSessionExpired, notifySessionExpired } from "./session-expiry";

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  // The whole session rides on the httpOnly `session` / `refresh_token` cookies
  // the API sets, so every request has to carry credentials.
  withCredentials: true,
});

// There is deliberately NO Authorization header here.
//
// The access token was previously kept in `localStorage.token` and sent as a
// bearer. This app moved to httpOnly cookies, but the request interceptor that
// read that key survived the port — and `requireAuth` on the server prefers the
// bearer header over the cookie:
//
//     const token = bearer || req.cookies?.[SESSION_COOKIE];
//
// So on any browser that still had a stale `token` from the old app on the same
// host, that dead value overrode a perfectly valid cookie and 401'd every
// request. Refresh then *succeeded* (it reads the cookie, not the header), the
// retry re-attached the same dead bearer, 401'd again, and we bounced to login
// with the cookies still intact — which the proxy read as "logged in" and sent
// us straight back. Browsers without the leftover key were fine, which is what
// made the failure look random. The cookie is the only credential now.

// Retire the key itself, so it cannot come back. Not reading it is enough to fix
// this app, but the value sits in the same localStorage for as long as the
// browser keeps it, and anything that reads `token` on this host would pick a
// dead credential back up. Note the trade: on a machine that also runs a legacy
// app against localhost, the two apps share an origin, so this signs the
// user out of that app the first time this one loads.
// The try/catch is not defensive padding: this runs at module scope, and simply
// *touching* localStorage throws a SecurityError when storage is blocked (a
// sandboxed iframe, Safari with cookies denied). An uncaught throw here would
// take down every page that imports the api client, which is all of them.
if (typeof window !== "undefined") {
  try {
    window.localStorage.removeItem("token");
  } catch {
    // Storage unavailable — nothing to retire, and nothing could have read it.
  }
}

/**
 * Why the person was sent back to the login screen, for the login page to show.
 *
 * Handed over in sessionStorage rather than a query parameter because it has to
 * survive the redirect to /auth/login without staying in the address bar, where
 * it would be re-shown on every later visit. sessionStorage is also cleared when
 * the tab closes, which is the right lifetime for it.
 */
export const SIGN_OUT_REASON_KEY = "auth:signOutReason";

function rememberSignOutReason(message: string) {
  try {
    window.sessionStorage.setItem(SIGN_OUT_REASON_KEY, message);
  } catch {
    // Private-browsing modes can refuse this. The redirect still has to happen —
    // losing the explanation is much better than leaving them in a dead session.
  }
}

const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

// The access token in the `session` cookie lives for hours; the `refresh_token`
// cookie beside it lives for days. A 401 on an ordinary request therefore means
// "trade the refresh token for a new access token and try again", and only a
// failed trade ends the session.
//
// These endpoints are never retried through a refresh: the refresh call itself
// (it would recurse), and the ones that establish or end a session, where a 401
// is the actual answer rather than a stale credential.
const REFRESH_URL = "/api/user/refresh";
const NO_REFRESH_PATHS = [REFRESH_URL, "/api/user/login", "/api/user/register", "/api/user/logout"];

type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

function skipsRefresh(url: string | undefined) {
  return !!url && NO_REFRESH_PATHS.some((path) => url.includes(path));
}

// A page that loses its token fires several requests that all 401 together.
// They share one refresh call — sending one each would rotate the refresh token
// out from under the others, and the server treats a rotated token presented
// again outside its grace window as theft and revokes the whole session.
let refreshInFlight: Promise<void> | null = null;

function refreshSession() {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post(REFRESH_URL)
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function endSession(message: string) {
  rememberSignOutReason(message);
  notifySessionExpired();
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string; message?: string }>) => {
    const status = error.response?.status;

    if (typeof window === "undefined") {
      return Promise.reject(error);
    }

    // Any 403 — the server says this session has no permission. Could be a
    // disabled account, a CSRF rejection, or a role change that took effect
    // while the session was live. Either way the session is no longer usable.
    if (status === 403 && !window.location.pathname.startsWith("/auth")) {
      const message =
        error.response?.data?.code === "account_disabled"
          ? (error.response?.data?.message ?? "This account has been disabled by your team admin.")
          : "You do not have access. Please sign in again.";
      endSession(message);
      return Promise.reject(error);
    }

    if (status === 401) {
      const original = error.config as RetriableConfig | undefined;

      // Already signed out: refreshing now would only race the logout that is
      // clearing the cookies.
      if (isSessionExpired()) {
        return Promise.reject(error);
      }

      if (original && !original._authRetried && !skipsRefresh(original.url)) {
        original._authRetried = true;
        try {
          await refreshSession();
        } catch (refreshError) {
          // A 401/403 from the refresh call has already been through this
          // interceptor and ended the session. Any other client error (400:
          // no refresh cookie at all) is just as final. A 5xx or no response
          // says nothing about the session, so the user is left signed in.
          const refreshStatus = (refreshError as AxiosError).response?.status;
          if (refreshStatus !== undefined && refreshStatus < 500 && !isSessionExpired()) {
            endSession(SESSION_EXPIRED_MESSAGE);
          }
          return Promise.reject(error);
        }
        return api.request(original);
      }

      // The refresh call itself, a request that still 401'd after a successful
      // refresh, or a session endpoint: nothing left to try.
      endSession(SESSION_EXPIRED_MESSAGE);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
